import { call, put, select, takeEvery, takeLeading } from 'typed-redux-saga'
import { routerActions } from 'connected-react-router'
import { errorHandler, loadersSlice, notifyMessageCreator } from 'Modules'
import { get } from 'lodash'
import { Deserialize } from 'cerialize'
import { FrontUrls } from 'Constants/FrontUrls'
import { RequestParams, ResponseCatalog, FormErrorModel } from 'Models'
import { Callback } from 'Src/types/utilTypes'
import { surveySelector, SurveyModel, surveyApi, SurveyNS, surveySlice } from 'Pages/survey'

function* getAllSurvey(params?: RequestParams, clear?: boolean, callback?: Callback) {
	const { page, per_page, query, ...fields } = params
	const requestParams = new RequestParams(clear ? 1 : page, per_page, query, fields)
	if (clear) {
		yield* put(surveySlice.actions.clearSurvey())
	}
	const res = yield* call(surveyApi.getAll, requestParams)
	const items = new ResponseCatalog<SurveyModel>(res)
	items.entries = res.entries.map((entry): SurveyModel => Deserialize(entry, SurveyModel))
	if (!clear) {
		const oldItems = yield* select(surveySelector.getSurveyDataEntries)
		items.entries = oldItems.concat(items.entries)
	}
	yield* put(surveySlice.actions.setOne(items))
	yield* put(surveySlice.actions.setParams(requestParams))
	callback?.()
}

function* getAll(action: SurveyNS.GetSurvey) {
	try {
		const { params, clear, callback } = action.payload
		yield* call(getAllSurvey, params, clear, callback)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getOne(action: SurveyNS.GetOne) {
	try {
		const res = yield* call(surveyApi.getOne, action.payload)
		const current = new ResponseCatalog<SurveyModel>()
		current.entries = [res]
		yield* put(surveySlice.actions.setOne(current))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* createOne(action: SurveyNS.CreateOne) {
	try {
		const { data } = action.payload
		const res = yield* call(surveyApi.createOne, data)
		yield* call(getAllSurvey)
		yield* call(notifyMessageCreator, 'common.survey_create.success', { data: [res.title] })
		yield* put(routerActions.push(FrontUrls.survey.survey_edit.replace(':id', res.id.toString())))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* updateOne(action: SurveyNS.UpdateOne) {
	try {
		const { id, data, callback } = action.payload
		const res = yield* call(surveyApi.updateOne, id, data)
		yield* call(notifyMessageCreator, 'common.survey_update.success', { data: [res.title] })
		callback?.()
		yield* put(surveySlice.actions.getOne(id))
	} catch (error) {
		yield* call(errorHandler, error)
		const reducedErr = get(error, 'data.errors', []).reduce((obj, item): FormErrorModel => {
			return { ...obj, ...item }
		}, {})
		yield* put(surveySlice.actions.setSurveyFormError(reducedErr))
	}
}

function* deleteOne(action: SurveyNS.DeleteOne) {
	try {
		const { callback, id } = action.payload
		yield* call(surveyApi.deleteOne, id)
		yield* call(notifyMessageCreator, 'common.delete_request.success')
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* downloadSurvey(action: SurveyNS.DownloadSurveyResults) {
	try {
		const { id, params, callback } = action.payload
		const res = yield* call(surveyApi.downloadSurveyResults, id, params)
		callback?.(res)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* showSurveyResults(action: SurveyNS.ShowSurveyResults) {
	try {
		const { id, filters, callback } = action.payload
		const res = yield* call(surveyApi.showSurveyResults, id, filters)
		yield* put(surveySlice.actions.setSurveyResult(res.results))
		callback?.(res)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* uploadSurveyFile(action: SurveyNS.UploadSurveyFile) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, file, callback } = action.payload
		const res = yield* call(surveyApi.loadSurveyDocument, {
			id,
			file,
		})
		yield* call(notifyMessageCreator, 'common.file_upload.success')
		callback?.(res)
	} catch (error) {
		const { errorCallback } = action.payload
		const errors = error?.data?.errors
		errorCallback?.(Object.keys(errors?.[0] ?? {}).map((key) => errors[0][key]))
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* deleteSurveyFile(action: SurveyNS.DeleteSurveyFile) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { file, callback } = action.payload
		yield* call(surveyApi.deleteSurveyFile, file.file)
		yield* call(notifyMessageCreator, 'common.file_delete.success')
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* updateSurveyFile(action: SurveyNS.UpdateSurveyFile) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { deleteFile, id, file, callback } = action.payload
		yield* call(surveyApi.deleteSurveyFile, deleteFile.file)
		const res = yield* call(surveyApi.loadSurveyDocument, {
			id,
			file,
		})
		yield* put(surveySlice.actions.getOne(id))
		yield* call(notifyMessageCreator, 'common.file_upload.success')
		callback?.(res)
	} catch (error) {
		const { errorCallback } = action.payload
		const errors = error?.data?.errors
		errorCallback?.(Object.keys(errors?.[0] ?? {}).map((key) => errors[0][key]))
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

export const surveySaga = function* saga() {
	yield* takeLeading(surveySlice.actions.getSurvey.type, getAll)
	yield* takeLeading(surveySlice.actions.getOne.type, getOne)
	yield* takeEvery(surveySlice.actions.createOne.type, createOne)
	yield* takeEvery(surveySlice.actions.updateOne.type, updateOne)
	yield* takeEvery(surveySlice.actions.deleteOne.type, deleteOne)
	yield* takeEvery(surveySlice.actions.downloadSurveyResults.type, downloadSurvey)
	yield* takeEvery(surveySlice.actions.showSurveyResults.type, showSurveyResults)
	yield* takeEvery(surveySlice.actions.uploadSurveyFile.type, uploadSurveyFile)
	yield* takeEvery(surveySlice.actions.deleteSurveyFile.type, deleteSurveyFile)
	yield* takeEvery(surveySlice.actions.updateSurveyFile.type, updateSurveyFile)
}
